// ---------------------------------------------------------------------------
// Dashboard "Vendas a prazo" — helpers de exibição (0084 §5, regras NÃO
// negociáveis). Um helper por regra, usado em toda a tela: se a formatação
// diverge entre dobras, o operador desconfia do número.
//
// A regra-mãe: **null ≠ 0**. Toda taxa, `atraso_medio_dias`, `dso_dias` e ponto
// de safra pode vir `null` quando não há denominador ou o período ainda não
// ocorreu. Renderiza-se "—". Nunca 0, nunca 0%, nunca NaN.
// ---------------------------------------------------------------------------

export const DASH = '—';

function isMissing(value) {
   return value === null || value === undefined || Number.isNaN(value);
}

// ─── Moeda ────────────────────────────────────────────────────────────────
// `4129344.71` → "R$ 4.129.344,71" (2 casas sempre).
const currencyFormatter = new Intl.NumberFormat('pt-BR', {
   style: 'currency',
   currency: 'BRL',
   minimumFractionDigits: 2,
   maximumFractionDigits: 2,
});

export function formatCurrency(value) {
   if (isMissing(value)) return DASH;
   return currencyFormatter.format(Number(value));
}

// Versão compacta para eixos e rótulos apertados: "R$ 4,1 mi" / "R$ 546 mil".
export function formatCurrencyShort(value) {
   if (isMissing(value)) return DASH;
   const number = Number(value);
   const abs = Math.abs(number);
   if (abs >= 1000000) {
      return `R$ ${(number / 1000000).toLocaleString('pt-BR', {
         maximumFractionDigits: 1,
      })} mi`;
   }
   if (abs >= 1000) {
      return `R$ ${(number / 1000).toLocaleString('pt-BR', {
         maximumFractionDigits: 0,
      })} mil`;
   }
   return currencyFormatter.format(number);
}

// ─── Percentual ───────────────────────────────────────────────────────────
// ⚠️ Já vem em base 100 (`82.98` = 82,98%). NÃO multiplicar por 100.
export function formatPercent(value, digits = 2) {
   if (isMissing(value)) return DASH;
   return `${Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
   })}%`;
}

// ─── Números ──────────────────────────────────────────────────────────────
export function formatNumber(value, digits = 0) {
   if (isMissing(value)) return DASH;
   return Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
   });
}

// ─── Plural (0084 §5.7 — helper ÚNICO) ────────────────────────────────────
// "1 título" / "2 títulos" / "0 título"? Não: zero vai no plural, como em
// PT-BR corrente ("0 títulos").
export function pluralize(count, singular, plural) {
   if (isMissing(count)) return DASH;
   const n = Number(count);
   return `${formatNumber(n)} ${Math.abs(n) === 1 ? singular : plural}`;
}

export function pluralTitulos(count) {
   return pluralize(count, 'título', 'títulos');
}

// ─── Datas ────────────────────────────────────────────────────────────────
//
// ⚠️ NÃO usar `new Date('2026-08-07')`: o JS lê a string ISO curta como UTC
// meia-noite e, em America/Sao_Paulo (UTC-3), o `toLocaleDateString` devolve o
// DIA ANTERIOR. Como o backend só manda data (sem hora), o certo é fatiar a
// string — sem objeto Date, sem fuso, sem drift.
export function formatIsoDate(value) {
   const text = String(value ?? '').trim();
   if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return DASH;
   const [year, month, day] = text.slice(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

const MONTH_ABBR = [
   'jan',
   'fev',
   'mar',
   'abr',
   'mai',
   'jun',
   'jul',
   'ago',
   'set',
   'out',
   'nov',
   'dez',
];

// "2026-02" → "fev/2026" (rótulo de safra e de carga histórica).
export function formatMonthLabel(value) {
   const text = String(value ?? '').trim();
   if (!/^\d{4}-\d{2}/.test(text)) return DASH;
   const [year, month] = text.slice(0, 7).split('-');
   const abbr = MONTH_ABBR[Number(month) - 1];
   return abbr ? `${abbr}/${year}` : DASH;
}

// ─── Aritmética de data em ISO puro (sem fuso) ────────────────────────────
//
// Toda a matemática de período parte do `reference_date` do SERVIDOR. Usa
// Date.UTC para não escorregar de dia e devolve YYYY-MM-DD.
export function addDaysIso(isoDate, days) {
   const text = String(isoDate ?? '').trim();
   if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
   const [year, month, day] = text.slice(0, 10).split('-').map(Number);
   const stamp = Date.UTC(year, month - 1, day + days);
   return new Date(stamp).toISOString().slice(0, 10);
}

export function startOfMonthIso(isoDate) {
   const text = String(isoDate ?? '').trim();
   if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
   return `${text.slice(0, 7)}-01`;
}

export function startOfYearIso(isoDate) {
   const text = String(isoDate ?? '').trim();
   if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
   return `${text.slice(0, 4)}-01-01`;
}

// ─── Fallback de exibição ─────────────────────────────────────────────────
export function orDash(value) {
   const text = String(value ?? '').trim();
   return text || DASH;
}
